'use client';

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMotionValue, useTransform, useMotionValueEvent, animate, cubicBezier } from 'framer-motion';
import type { AnimationPlaybackControls } from 'framer-motion';
import { HERO_REEL_CHROME } from './hero-reel-chrome';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * HeroCoverReel — the hero mockup with its two photos cycling on a masked wipe.
 *
 * Drop-in replacement for the hero <video>: same 393:357 artwork (the videos were literally this
 * mockup at 2x/2.5x), transparent background so the section colour shows through, and it fills
 * whatever box it is given.
 *
 * THE TRANSITION — nothing translates except the mask:
 *   The next photo is painted at its FINAL position, at full opacity, directly on top of the
 *   current one (which never moves and never fades). It is hidden by an SVG <mask> whose content
 *   sits off to the right; the mask group slides right→left, so the new photo is uncovered in
 *   place. The pixels of both photos are static — only the mask edge travels.
 *
 * framer-motion drives ONE master timeline (`progress`, looping 0→1 over LOOP_MS) and derives two
 * staggered wipes from it. The values are written onto the mask groups' transform attribute frame
 * by frame (useMotionValueEvent → setAttribute), so the SVG is re-driven every frame.
 */

const VB_W = 393;
const VB_H = 357;

const BASE = '/images/comix/homepage/v2';
/**
 * Real customer photos — a wide/group shot (BIG_DECK, main slot) paired with a close-up crop
 * of the same numbered example (SMALL_DECK, inset slot), shown together each cycle. The two
 * slots' step counters (`mainStep`/`insetStep`) always advance in lockstep (see `advance`
 * below — both derive from the same master `progress` timeline and transition on the same
 * hold/settle boundaries), so indexing both decks with the same step number naturally keeps
 * each pair together instead of drifting apart.
 */
const BIG_DECK = [
  `${BASE}/hero-big-1.avif`,
  `${BASE}/hero-big-2.avif`,
  `${BASE}/hero-big-3.avif`,
  `${BASE}/hero-big-4.avif`,
  `${BASE}/hero-big-6.avif`,
  `${BASE}/hero-big-7-v2.avif`,
  `${BASE}/hero-big-8.avif`,
  `${BASE}/hero-big-9.avif`,
];
const SMALL_DECK = [
  `${BASE}/hero-small-1.avif`,
  `${BASE}/hero-small-2-v2.avif`,
  `${BASE}/hero-small-3.avif`,
  `${BASE}/hero-small-4.avif`,
  `${BASE}/hero-small-6.avif`,
  `${BASE}/hero-small-7-v3.avif`,
  `${BASE}/hero-small-8.avif`,
  `${BASE}/hero-small-9.avif`,
];
const N = BIG_DECK.length;

const pickMain = (n: number) => BIG_DECK[((n % N) + N) % N];
const pickInset = (n: number) => SMALL_DECK[((n % N) + N) % N];

// Split points in the mockup: draw the main photo where the main gray placeholder is, and the
// inset photo where the inset gray placeholder is — so z-order (frame → main → inset → arrow) holds.
const MAIN_GRAY = '<path d="M41 23.5H286V325.5H41V23.5Z" fill="#D9D9D9"/>';
const INSET_GRAY = '<rect x="251" y="197.5" width="99" height="98" rx="15" fill="#D9D9D9"/>';

// Photo slots, in mockup coordinates.
const MAIN = { x: 41, y: 23.5, w: 245, h: 302 };
const INSET = { x: 251, y: 197.5, w: 99, h: 98, rx: 15 };

// Soft leading edge on each wipe (px, mockup coords). The mask travels this much extra at both
// ends so the feather is fully clear of the slot at rest.
const FEATHER_MAIN = 18;
const FEATHER_INSET = 10;

// Mask travel: from fully clear of the slot's RIGHT edge, to fully past its LEFT edge.
const MAIN_FROM = MAIN.x + MAIN.w + FEATHER_MAIN;
const MAIN_TO = MAIN.x - FEATHER_MAIN;
const INSET_FROM = INSET.x + INSET.w + FEATHER_INSET;
const INSET_TO = INSET.x - FEATHER_INSET;

const lerp = (from: number, to: number, t: number) => from + (to - from) * t;

// Transport badge, centred on the main cover and shown only on hover/focus.
const BADGE = { cx: MAIN.x + MAIN.w / 2, cy: MAIN.y + MAIN.h / 2, r: 30 };

// ---- timing (ms) ----------------------------------------------------------
const LOOP_MS = 3000;    // hold + wipe + settle
const WIPE_MS = 1000;    // how long one cover takes to be uncovered
const STAGGER_MS = 150;  // the inset leads; the main photo starts this much later
/**
 * Dwell on the fully-revealed cover before the loop point. Without it the wipe finished exactly AS
 * the loop restarted, so the new cover was never on screen at rest — it read as a jump cut. The
 * settle also gives the swap below a margin: even if the index commit slipped a frame, it would
 * land inside this window where both layers show the same image.
 */
const SETTLE_MS = 400;

type Props = {
  className?: string;
  /** Announced to screen readers. Pass '' to mark the reel decorative. */
  label?: string;
};

export default function HeroCoverReel({
  className = '',
  label = 'Create personalized comic books starring your favorite people with AI',
}: Props) {
  /**
   * Each slot keeps TWO independent step counters, one per layer, instead of a single index that
   * advances at the loop point.
   *
   * The masks are written synchronously from the motion value; React state is committed on its own
   * schedule. Anything that has to change *at* the boundary therefore races the mask snap, and
   * whichever loses shows a wrong cover for a frame — a flash back to the previous one, or a skip
   * forward to the next. That race is the jump cut.
   *
   * So neither layer changes at the boundary. Each is advanced only while it is invisible:
   *   - `resting` is promoted DURING THE SETTLE, hidden under the fully-revealed incoming layer
   *     (a ~400ms window).
   *   - `incoming` is advanced DURING THE HOLD, while the mask is shut and it is not drawn at all
   *     (a ~1450ms window).
   * A late commit lands inside the window, so it cannot be seen, and the ordering stops mattering.
   */
  const [mainStep, setMainStep] = useState({ resting: 0, incoming: 1 });
  const [insetStep, setInsetStep] = useState({ resting: 0, incoming: 1 });

  // Click-to-pause. `controls` is null while reduced motion is on (nothing is running), which is
  // also what makes the reel non-interactive in that mode.
  const controlsRef = useRef<AnimationPlaybackControls | null>(null);
  const [paused, setPaused] = useState(false);
  // Drives the badge + a11y affordances. State, not the ref, because the ref is populated in an
  // effect and would not re-render the wrapper.
  const [interactive, setInteractive] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isMobile = useIsMobile();
  // Mobile only: the badge appears on tap. While the reel is RUNNING it self-hides 2s later
  // so it stops sitting on the cover; while it's PAUSED it stays (you need the ▶ to know a
  // tap resumes it). Desktop is unchanged — hover/focus drives it.
  const [tappedAt, setTappedAt] = useState(0);
  useEffect(() => {
    if (!isMobile || tappedAt === 0 || paused) return;
    const t = setTimeout(() => setTappedAt(0), 2000);
    return () => clearTimeout(t);
  }, [isMobile, tappedAt, paused]);

  // Ids must be unique per instance — two reels on one page would otherwise share masks.
  const uid = useId().replace(/:/g, '');
  const id = (name: string) => `${name}-${uid}`;

  const mainMaskRef = useRef<SVGGElement>(null);
  const insetMaskRef = useRef<SVGGElement>(null);

  // ---- framer-motion timeline --------------------------------------------
  const progress = useMotionValue(0);
  const bez = cubicBezier(0.45, 0, 0.15, 1);
  // One cycle: hold the current cover → wipe → settle on the new one → loop.
  // The inset leads, the main photo follows STAGGER_MS later — same wipe duration for both,
  // so the pair reads as one gesture with the small offset giving it depth.
  const mainEnd = (LOOP_MS - SETTLE_MS) / LOOP_MS;                          // 2600ms
  const mainStart = (LOOP_MS - SETTLE_MS - WIPE_MS) / LOOP_MS;              // 1600ms
  const insetEnd = (LOOP_MS - SETTLE_MS - STAGGER_MS) / LOOP_MS;            // 2450ms
  const insetStart = (LOOP_MS - SETTLE_MS - WIPE_MS - STAGGER_MS) / LOOP_MS; // 1450ms
  const wipeInset = useTransform(progress, [0, insetStart, insetEnd, 1], [0, 0, 1, 1], { ease: bez });
  const wipeMain = useTransform(progress, [0, mainStart, mainEnd, 1], [0, 0, 1, 1], { ease: bez });

  /**
   * Frame-by-frame: push each wipe onto its SVG mask group — the only thing that moves — and use
   * the same value's two rest points to advance the layers while they are invisible.
   *
   * The transform output is clamped, so it sits at exactly 1 for the whole settle and exactly 0 for
   * the whole hold; 'change' therefore fires once on entering each. Returning the previous object
   * unchanged makes React bail out, so these fire at most one re-render per cycle per slot.
   */
  const advance = (
    t: number,
    set: React.Dispatch<React.SetStateAction<{ resting: number; incoming: number }>>
  ) => {
    if (t >= 1) {
      // Settle: the incoming layer covers the slot, so swapping the layer underneath is unseen.
      set((s) => (s.resting === s.incoming ? s : { ...s, resting: s.incoming }));
    } else if (t <= 0) {
      // Hold: the mask is shut, so the incoming layer can be re-pointed without being drawn.
      set((s) => (s.incoming === s.resting + 1 ? s : { resting: s.resting, incoming: s.resting + 1 }));
    }
  };

  useMotionValueEvent(wipeInset, 'change', (t) => {
    insetMaskRef.current?.setAttribute('transform', `translate(${lerp(INSET_FROM, INSET_TO, t)},0)`);
    advance(t, setInsetStep);
  });
  useMotionValueEvent(wipeMain, 'change', (t) => {
    mainMaskRef.current?.setAttribute('transform', `translate(${lerp(MAIN_FROM, MAIN_TO, t)},0)`);
    advance(t, setMainStep);
  });

  useEffect(() => {
    // Respect the OS setting: hold a single resting cover rather than looping.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const controls = animate(progress, 1, {
      duration: LOOP_MS / 1000,
      ease: 'linear',
      // Nothing to do at the loop point: both layers were already advanced while invisible, so the
      // masks simply snap shut over a resting layer that is showing the correct cover.
      repeat: Infinity,
    });
    controlsRef.current = controls;
    setInteractive(true);
    return () => { controls.stop(); controlsRef.current = null; setInteractive(false); };
  }, [progress]);

  // Toggle. Pausing freezes the timeline wherever it is — mid-wipe included — and resuming picks
  // up from that exact point rather than restarting the loop.
  const toggle = useCallback(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    setPaused((wasPaused) => {
      if (wasPaused) controls.play();
      else controls.pause();
      return !wasPaused;
    });
    setTappedAt(Date.now()); // re-arm the mobile auto-hide on every tap
  }, []);

  // Slice the mockup around the two placeholders so the photos slot in at the right z-index.
  const parts = useMemo(() => {
    const s = HERO_REEL_CHROME;
    const mIdx = s.indexOf(MAIN_GRAY);
    const mEnd = mIdx === -1 ? -1 : s.indexOf('</g>', mIdx) + 4; // after the shadow group closes
    const iIdx = s.indexOf(INSET_GRAY);
    const iEnd = iIdx === -1 ? -1 : iIdx + INSET_GRAY.length;
    if (mEnd === -1 || iEnd === -1) return { a: s, b: '', c: '' };
    return { a: s.slice(0, mEnd), b: s.slice(mEnd, iEnd), c: s.slice(iEnd) };
  }, []);

  return (
    <div
      className={className}
      onClick={interactive ? toggle : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
            }
          : undefined
      }
      onMouseEnter={interactive ? () => setHovered(true) : undefined}
      onMouseLeave={interactive ? () => setHovered(false) : undefined}
      onFocus={interactive ? () => setHovered(true) : undefined}
      onBlur={interactive ? () => setHovered(false) : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? paused : undefined}
      aria-label={interactive ? (paused ? 'Resume the animation' : 'Stop the animation') : undefined}
      style={interactive ? { cursor: 'pointer' } : undefined}
    >
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full"
        role={label ? 'img' : undefined}
        aria-label={label || undefined}
        aria-hidden={label ? undefined : true}
      >
        <defs>
          <linearGradient id={id('feather')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#fff" stopOpacity="0" />
            <stop offset="1" stopColor="#fff" stopOpacity="1" />
          </linearGradient>

          {/* slot shapes — the photos are always clipped to these, moving or not */}
          <clipPath id={id('mainClip')}><rect x={MAIN.x} y={MAIN.y} width={MAIN.w} height={MAIN.h} /></clipPath>
          <clipPath id={id('insetClip')}><rect x={INSET.x} y={INSET.y} width={INSET.w} height={INSET.h} rx={INSET.rx} /></clipPath>

          {/* the travelling masks — same motion, offset by STAGGER_MS */}
          <mask id={id('mainWipe')} maskUnits="userSpaceOnUse" x={0} y={0} width={VB_W} height={VB_H}>
            <g ref={mainMaskRef} transform={`translate(${MAIN_FROM},0)`}>
              <rect x={-FEATHER_MAIN} y={0} width={FEATHER_MAIN} height={VB_H} fill={`url(#${id('feather')})`} />
              <rect x={0} y={0} width={MAIN.w + 2 * FEATHER_MAIN} height={VB_H} fill="#fff" />
            </g>
          </mask>
          <mask id={id('insetWipe')} maskUnits="userSpaceOnUse" x={0} y={0} width={VB_W} height={VB_H}>
            <g ref={insetMaskRef} transform={`translate(${INSET_FROM},0)`}>
              <rect x={-FEATHER_INSET} y={0} width={FEATHER_INSET} height={VB_H} fill={`url(#${id('feather')})`} />
              <rect x={0} y={0} width={INSET.w + 2 * FEATHER_INSET} height={VB_H} fill="#fff" />
            </g>
          </mask>
        </defs>

        {/* mockup chrome up to & including the main-card shadow (behind the main photo) */}
        <g dangerouslySetInnerHTML={{ __html: parts.a }} />

        {/* MAIN CANVAS — outgoing photo holds its place; incoming photo is uncovered on top of it */}
        <image
          href={pickMain(mainStep.resting)} x={MAIN.x} y={MAIN.y} width={MAIN.w} height={MAIN.h}
          clipPath={`url(#${id('mainClip')})`} preserveAspectRatio="xMidYMin slice"
        />
        <g mask={`url(#${id('mainWipe')})`}>
          <image
            href={pickMain(mainStep.incoming)} x={MAIN.x} y={MAIN.y} width={MAIN.w} height={MAIN.h}
            clipPath={`url(#${id('mainClip')})`} preserveAspectRatio="xMidYMin slice"
          />
        </g>

        {/* sheen + inset border/shadow (static, above the main photo, below the inset photo) */}
        <g dangerouslySetInnerHTML={{ __html: parts.b }} />

        {/* INSET CARD — same wipe, started STAGGER_MS earlier; border and shadow stay locked */}
        <image
          href={pickInset(insetStep.resting)} x={INSET.x} y={INSET.y} width={INSET.w} height={INSET.h}
          clipPath={`url(#${id('insetClip')})`} preserveAspectRatio="xMidYMid slice"
        />
        <g mask={`url(#${id('insetWipe')})`}>
          <image
            href={pickInset(insetStep.incoming)} x={INSET.x} y={INSET.y} width={INSET.w} height={INSET.h}
            clipPath={`url(#${id('insetClip')})`} preserveAspectRatio="xMidYMid slice"
          />
        </g>

        {/* arrow + remaining chrome (static anchor) */}
        <g dangerouslySetInnerHTML={{ __html: parts.c }} />

        {/* Transport badge — centred on the main cover, only while hovering or focused. Clipped
            to the cover so it can never spill onto the page, and pointer-transparent so the
            wrapper keeps receiving the hover and the click. */}
        {interactive && (
          <g
            clipPath={`url(#${id('mainClip')})`}
            style={{
              opacity: (isMobile ? paused || tappedAt !== 0 : hovered) ? 1 : 0,
              transition: 'opacity 240ms ease',
              pointerEvents: 'none',
            }}
          >
            <circle cx={BADGE.cx} cy={BADGE.cy} r={BADGE.r} fill="black" fillOpacity={0.5} />
            {paused ? (
              <path
                d={`M${BADGE.cx - 9} ${BADGE.cy - 12} L${BADGE.cx + 13} ${BADGE.cy} L${BADGE.cx - 9} ${BADGE.cy + 12} Z`}
                fill="white"
              />
            ) : (
              <>
                <rect x={BADGE.cx - 10} y={BADGE.cy - 12} width={7} height={24} rx={2} fill="white" />
                <rect x={BADGE.cx + 3} y={BADGE.cy - 12} width={7} height={24} rx={2} fill="white" />
              </>
            )}
          </g>
        )}
      </svg>
    </div>
  );
}
